// src/pages/health/components/WorkoutEditorModal.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import {
    useCreateWorkout,
    useUpdateWorkout,
    useDayWorkoutsInvalidate,
} from "../../../hooks/useWorkouts";

import {
    saveMachineByTitle,
    saveCardioByTitle,
} from "../../../api/workoutSetting";

/* ───────── 옵션 ───────── */
const MACHINE_OPTIONS = [
    "데드 리프트",
    "레그익스텐션",
    "시티드 레그프레스",
    "시티드 레그컬",
    "시티드 체스트프레스",
    "버터플라이",
    "인클라인 프레스",
    "랫풀다운",
    "롱풀",
    "리어 델트플라이",
    "숄더프레스",
    "암컬",
    "디핑",
    "업도미널",
    "로타리 토르소",
    "이너/아웃싸이",
];
const CARDIO_OPTIONS = ["러닝머신", "자전거", "스텝머신"];

/* ───────── 매핑 ───────── */
function titleToType(t = "") {
    const s = String(t).toLowerCase();
    if (s.includes("러닝") || s.includes("tread")) return "treadmill";
    if (s.includes("자전") || s.includes("cycle") || s.includes("bike")) return "bike";   // 저장 시 cycle로 매핑
    if (s.includes("스텝") || s.includes("step")) return "stepper";
    return "machine";
}

/* 어떤 필드를 보일지: */
const FIELD_CONF = {
    machine: { weight: true, sets: true, reps: true },
    treadmill: { speed: true, duration: true, distance: true },
    bike: { watt: true, duration: true },
    stepper: { watt: true, duration: true },
};

/* ───────── 플랫 드롭다운 ───────── */
function FlatDropdown({ value, options, onChange, className = "" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);
    return (
        <div className={`relative ${className}`} ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50
                   flex items-center justify-between shadow-sm transition active:scale-[0.99]"
            >
                <span className="font-medium truncate">{value}</span>
                <ChevronDownIcon className={`w-5 h-5 text-gray-600 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <ul
                    role="listbox"
                    className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-xl
                     border border-gray-200 bg-white shadow-md"
                >
                    {options.map((opt) => (
                        <li key={opt} role="option" aria-selected={value === opt}>
                            <button
                                type="button"
                                onClick={() => {
                                    onChange(opt);
                                    setOpen(false);
                                }}
                                className={`w-full h-10 px-3 text-left transition ${value === opt ? "bg-[#E8F1FF] text-[#1F4FD3] font-semibold" : "hover:bg-gray-100 text-gray-800"
                                    }`}
                            >
                                {opt}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* ───────── 메인 모달 ───────── */
export default function MachineSettingModal({
    open, mode, date, initial, onClose,
    context = "workout",
    onSubmit,
}) {
    const isEdit = mode === "edit";
    const invalidate = useDayWorkoutsInvalidate();

    /* 카테고리: '근력 운동' | '유산소 운동' (UI 라벨) */
    const normalizeCategoryLabel = (raw) => {
        if (raw === "cardio" || raw === "유산소 운동" || raw === "유산소운동") return "유산소 운동";
        return "근력 운동"; // 'strength' | '웨이트' 등은 모두 근력으로 수렴
    };
    const [category, setCategory] = useState(normalizeCategoryLabel(initial?.category));

    /* 공통/선택된 종목 */
    const [title, setTitle] = useState(initial?.title || "");

    /* 웨이트 전용 */
    const [weightKg, setWeightKg] = useState(initial?.sets?.[0]?.weight_kg ?? "");
    const [sets, setSets] = useState(initial?.sets?.length ?? "");
    const [reps, setReps] = useState(initial?.sets?.[0]?.reps ?? "");

    /* 유산소 공통 */
    const [duration, setDuration] = useState(initial?.duration_min ?? "");

    /* 러닝머신 전용 */
    const [speed, setSpeed] = useState(initial?.speed_kph ?? "");
    const [distance, setDistance] = useState(initial?.distance_km ?? "");
    const [targetHr, setTargetHr] = useState(
        initial?.target_hr_bpm ??
        initial?.cardio?.target_hr_bpm ??
        ""
    );

    /* 자전거/스텝머신 전용 */
    const [watt, setWatt] = useState(initial?.watt ?? initial?.power_watt ?? initial?.watt_w ?? "");

    const [notes, setNotes] = useState(initial?.notes ?? "");

    const createMut = useCreateWorkout();
    const updateMut = useUpdateWorkout();

    /* 카테고리 바뀌면 기본 종목 채우기 */
    useEffect(() => {
        if (!open) return;
        const opts = category === "근력 운동" ? MACHINE_OPTIONS : CARDIO_OPTIONS;
        if (!title) setTitle(opts[0]);
    }, [open, category, title]);

    const options = useMemo(() => (category === "근력 운동" ? MACHINE_OPTIONS : CARDIO_OPTIONS), [category]);

    const exType = useMemo(() => titleToType(title || options[0]), [title, options]);
    const vis = FIELD_CONF[exType];

    const saving = createMut.isPending || updateMut.isPending;

    useEffect(() => {
        if (exType !== "machine") {
            setWeightKg("");
            setSets("");
            setReps("");
        }
        if (exType !== "treadmill") {
            setSpeed("");
            setDistance("");
            setTargetHr("");
        }
        if (exType !== "bike" && exType !== "stepper") {
            setWatt("");
        }
    }, [exType]);


    const buildLocalPayload = () => {
        const isCardio = category === "유산소 운동";

        if (!isCardio) {
            return {
                date,
                title,
                category: "strength",
                kind: "strength",
                notes: notes || undefined,
                weight_kg: weightKg !== "" ? Number(weightKg) : undefined,
                sets: sets !== "" ? Number(sets) : undefined,
                reps: reps !== "" ? Number(reps) : undefined,
            };
        }

        return {
            date,
            title,
            category: "cardio",
            kind: "cardio",
            notes: notes || undefined,
            duration_min: duration !== "" ? Number(duration) : undefined,
            cardio:
                exType === "treadmill"
                    ? {
                        speed_kph: speed !== "" ? Number(speed) : undefined,
                        distance_km: distance !== "" ? Number(distance) : undefined,
                        target_hr_bpm: targetHr !== "" ? Number(targetHr) : undefined,
                    }
                    : {
                        watt: watt !== "" ? Number(watt) : undefined,
                    },
        };
    };


    const onSave = async () => {
        const isCardio = category === "유산소 운동";

        if (context === "settings") {
            try {
                const payload = buildLocalPayload();

                if (!isCardio) {
                    const saved = await saveMachineByTitle(title, payload);
                    onSubmit?.({
                        title,
                        sets: saved.sets
                            ? Array.from({ length: saved.sets }).map((_, i) => ({
                                set_no: i + 1,
                                weight_kg: saved.weight_kg ?? undefined,
                                reps: saved.reps ?? undefined,
                            }))
                            : undefined,
                        weight_kg: saved.weight_kg ?? undefined,
                        reps: saved.reps ?? undefined,
                    });
                } else {
                    const saved = await saveCardioByTitle(title, payload);
                    onSubmit?.({
                        title,
                        duration_min: saved.duration_min ?? undefined,
                        cardio:
                            (exType === "treadmill")
                                ? {
                                    speed_kph: saved.speed_kph ?? undefined,
                                    distance_km: saved.distance_km ?? undefined,
                                    target_hr_bpm: saved.target_hr_bpm ?? undefined,
                                }
                                : {
                                    watt_w: saved.watt_w ?? undefined,
                                },
                        speed_kph: saved.speed_kph ?? undefined,
                        distance_km: saved.distance_km ?? undefined,
                        watt_w: saved.watt_w ?? undefined,
                        target_hr_bpm: saved.target_hr_bpm ?? undefined,
                    });
                }

                onClose();
                return;
            } catch (e) {
                console.error("머신/카디오 세팅 저장 실패:", e);
                return; // 실패 시 닫지 않음
            }
        }

        const apiPayload = {
            date,
            title,
            category: isCardio ? "cardio" : "strength",
            kind: isCardio ? "cardio" : "strength",
            notes: notes || undefined,
            duration_min: duration !== "" ? Number(duration) : undefined,
            cardio: isCardio
                ? exType === "treadmill"
                    ? {
                        speed_kph: speed !== "" ? Number(speed) : undefined,
                        distance_km: distance !== "" ? Number(distance) : undefined,
                    }
                    : {
                        watt: watt !== "" ? Number(watt) : undefined,
                    }
                : undefined,
            sets:
                !isCardio && sets
                    ? Array.from({ length: Number(sets) }).map((_, i) => ({
                        set_no: i + 1,
                        weight_kg: weightKg !== "" ? Number(weightKg) : undefined,
                        reps: reps !== "" ? Number(reps) : undefined,
                    }))
                    : undefined,
        };

        try {
            if (isEdit && initial?.id) {
                await updateMut.mutateAsync({ id: initial.id, data: apiPayload });
            } else {
                await createMut.mutateAsync(apiPayload);
            }
            await invalidate(date);
            onClose();
        } catch (e) {
            console.error(e);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100]">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute left-1/2 top-1/2 w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2">
                <div className="rounded-2xl bg-white shadow-lg overflow-hidden">
                    {/* 헤더 */}
                    <div className="flex items-center justify-center h-14 relative">
                        <button
                            onClick={onClose}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-gray-100"
                        >
                            <XMarkIcon className="w-7 h-7 text-gray-500" />
                        </button>
                        <div className="font-semibold text-lg md:text-xl">{isEdit ? "세팅 수정" : "세팅 추가"}</div>
                    </div>

                    <div className="p-4 space-y-4">
                        {/* 분류 */}
                        <div>
                            <div className="mb-1 text-sm font-medium">운동 범주</div>
                            <FlatDropdown
                                value={category}
                                options={["근력 운동", "유산소 운동"]}
                                onChange={setCategory}
                            />
                        </div>

                        <div>
                            <div className="mb-1 text-sm font-medium">상세 운동 종목</div>
                            <FlatDropdown value={title || (category === "근력 운동" ? MACHINE_OPTIONS[0] : CARDIO_OPTIONS[0])} options={category === "근력 운동" ? MACHINE_OPTIONS : CARDIO_OPTIONS} onChange={setTitle} />
                        </div>

                        {exType === "machine" && (
                            <>
                                {/* 설정 무게 */}
                                <div className="relative">
                                    <div className="mb-1 text-sm font-medium">설정 무게</div>
                                    <input
                                        className="w-full h-11 rounded-xl border border-gray-200 px-3 pr-12 bg-gray-50"
                                        value={weightKg}
                                        onChange={(e) => setWeightKg(e.target.value.replace(/[^\d.]/g, ""))}
                                        inputMode="decimal"
                                    />
                                    <span className="pointer-events-none absolute right-3 top-11 -translate-y-1/2 text-gray-600 leading-none">
                                        kg
                                    </span>
                                </div>

                                {/* 설정 세트 수 (회/세트 한 줄 배치) */}
                                <div>
                                    <div className="mb-1 text-sm font-medium">설정 세트 수</div>

                                    <div className="grid [grid-template-columns:minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2 min-w-0">
                                        <div className="min-w-0">
                                            <input
                                                className="w-full h-11 rounded-xl border border-gray-200 px-3  bg-gray-50"
                                                value={reps}
                                                onChange={(e) => setReps(e.target.value.replace(/\D/g, ""))}
                                                inputMode="numeric"
                                            />
                                        </div>
                                        <span className="text-gray-700 select-none whitespace-nowrap">회</span>

                                        <div className="min-w-0">
                                            <input
                                                className="w-full h-11 rounded-xl border border-gray-200 px-3  bg-gray-50"
                                                value={sets}
                                                onChange={(e) => setSets(e.target.value.replace(/\D/g, ""))}
                                                inputMode="numeric"
                                            />
                                        </div>
                                        <span className="text-gray-700 select-none whitespace-nowrap">세트</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ───── 유산소: 러닝머신 ───── */}
                        {exType === "treadmill" && (
                            <>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <div className="mb-1 text-sm font-medium">속도(km/h)</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={speed}
                                            onChange={(e) => setSpeed(e.target.value.replace(/[^\d.]/g, ""))}
                                            inputMode="decimal"
                                        />
                                    </div>
                                    <div>
                                        <div className="mb-1 text-sm font-medium">시간(분)</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={duration}
                                            onChange={(e) => setDuration(e.target.value.replace(/\D/g, ""))}
                                            inputMode="numeric"
                                        />
                                    </div>
                                    <div>
                                        <div className="mb-1 text-sm font-medium">거리(km)</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={distance}
                                            onChange={(e) => setDistance(e.target.value.replace(/[^\d.]/g, ""))}
                                            inputMode="decimal"
                                        />
                                    </div>
                                </div>

                                {/* 🔹 목표 심박수 입력 */}
                                <div className="mt-2">
                                    <div className="mb-1 text-sm font-medium">목표 심박수(bpm)</div>
                                    <input
                                        className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                        value={targetHr}
                                        onChange={(e) =>
                                            setTargetHr(e.target.value.replace(/\D/g, ""))
                                        }
                                        inputMode="numeric"
                                    />
                                </div>
                            </>
                        )}


                        {(exType === "bike" || exType === "stepper") && (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <div className="mb-1 text-sm font-medium">Watt</div>
                                    <input
                                        className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                        value={watt}
                                        onChange={(e) => setWatt(e.target.value.replace(/[^\d.]/g, ""))}
                                        inputMode="numeric"
                                    />
                                </div>
                                <div>
                                    <div className="mb-1 text-sm font-medium">시간(분)</div>
                                    <input
                                        className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                        value={duration}
                                        onChange={(e) => setDuration(e.target.value.replace(/\D/g, ""))}
                                        inputMode="numeric"
                                    />
                                </div>
                            </div>
                        )}

                        <button
                            onClick={onSave}
                            disabled={saving}
                            className="w-full h-12 rounded-xl bg-[#5F9BFF] text-white disabled:opacity-50 text-lg md:text-xl"
                        >
                            변경 내용 저장
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
